import { AxiosError } from 'axios';
import {
  STATUS_FORBIDDEN,
  STATUS_UPGRADE_REQUIRED,
} from '../../../../axios/axios.constants';
import { eAddUserFailure } from './Team.enums';

/*
 * workspace-service rejects an add-user request with two different 403s and no
 * machine-readable code: a revoked membership (RequesterUserIsNotPartOfWorkspace)
 * and a role that cannot invite (RequesterUserCannotCreateInvitations). Only the
 * exception text tells them apart, so this is the one place that depends on the
 * backend's wording. BUG-809 tracks adding a real error code upstream; when it
 * lands, this needle is the only thing that changes.
 */
const NOT_IN_WORKSPACE_NEEDLE = 'is not part of workspace';

function readBackendMessage(error: AxiosError): string {
  const data: unknown = error.response?.data;
  if (typeof data !== 'object' || data === null) return '';

  const { message } = data as { message?: unknown };
  return typeof message === 'string' ? message : '';
}

/*
 * Classifies an add-user failure into the outcome the UI should communicate.
 * `liveUid` is the tab's live Firebase identity and `paintedUserId` the identity
 * the UI is rendering: when they disagree the browser is authenticating as
 * somebody other than the user it shows (BUG-801), which outranks every other
 * reading of a 403 — the request was rejected because it was signed by the
 * wrong account, not because of anything about the account on screen.
 *
 * Both ids are optional because either can be unresolved; a missing one can
 * never prove a mismatch, so it degrades to the backend's own reason.
 */
export function classifyAddUserFailure(params: {
  error: unknown;
  liveUid?: string;
  paintedUserId?: string;
}): eAddUserFailure {
  const { error, liveUid, paintedUserId } = params;

  if (!(error instanceof AxiosError)) return eAddUserFailure.UNKNOWN;

  const status = error.response?.status;

  if (status === STATUS_UPGRADE_REQUIRED) return eAddUserFailure.NO_SEATS;
  if (status !== STATUS_FORBIDDEN) return eAddUserFailure.UNKNOWN;

  if (liveUid && paintedUserId && liveUid !== paintedUserId) {
    return eAddUserFailure.SESSION_MISMATCH;
  }

  return readBackendMessage(error).includes(NOT_IN_WORKSPACE_NEEDLE)
    ? eAddUserFailure.NOT_IN_WORKSPACE
    : eAddUserFailure.NO_PERMISSION;
}

export type tAddUserFailureCopy = {
  key: string;
  defaultValue: string;
};

/*
 * The i18n key and Spanish fallback for each failure. Returned as data rather
 * than a translated string so this stays a pure function the tests can pin
 * without an i18n instance.
 */
export function addUserFailureCopy(
  failure: eAddUserFailure,
  error: unknown,
): tAddUserFailureCopy {
  switch (failure) {
    case eAddUserFailure.NO_SEATS:
      return {
        key: 'addUser.noSeatsError',
        defaultValue:
          'No hay asientos disponibles. Actualiza tu plan para agregar más usuarios.',
      };
    case eAddUserFailure.SESSION_MISMATCH:
      return {
        key: 'addUser.sessionMismatchError',
        defaultValue:
          'Tu sesión no corresponde al usuario mostrado. Te llevaremos a iniciar sesión de nuevo.',
      };
    case eAddUserFailure.NOT_IN_WORKSPACE:
      return {
        key: 'addUser.notPartOfWorkspaceError',
        defaultValue:
          'Tu usuario ya no forma parte de este workspace. Cierra sesión y vuelve a entrar con una cuenta con acceso.',
      };
    case eAddUserFailure.NO_PERMISSION:
      return {
        key: 'addUser.noPermissionError',
        defaultValue:
          'Tu usuario no tiene permisos para agregar usuarios en este workspace.',
      };
    default:
      return {
        key: 'addUser.unknownError',
        defaultValue: `Error al agregar usuario: ${String(error)}`,
      };
  }
}
